import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  FileText, Upload, CheckCircle2, Clock, AlertCircle, XCircle, PenLine, Download,
  ArrowRight, Loader2, ShieldCheck, Trash2, RefreshCw, ChevronRight,
} from "lucide-react";
import { PageHero } from "../components/FormKit";
import { useAuth } from "../context/AuthContext";
import {
  getApplicationPrograms, getMyApplications, startApplication, getApplication,
  saveApplication, uploadAppDocument, deleteAppDocument, signApplication,
  submitApplication, withdrawApplication, downloadAppReceipt, startSubCheckout,
} from "../lib/hub";

const STATUS_META = {
  draft: { label: "Draft", icon: FileText, cls: "bg-black/10 text-black/70" },
  submitted: { label: "Submitted", icon: Clock, cls: "bg-blue-100 text-blue-700" },
  under_review: { label: "Under Review", icon: Clock, cls: "bg-amber-100 text-amber-700" },
  more_info_required: { label: "More Info Needed", icon: AlertCircle, cls: "bg-orange-100 text-orange-700" },
  resubmitted: { label: "Resubmitted", icon: Clock, cls: "bg-blue-100 text-blue-700" },
  approved: { label: "Approved", icon: CheckCircle2, cls: "bg-green-100 text-green-700" },
  rejected: { label: "Not Approved", icon: XCircle, cls: "bg-red-100 text-red-700" },
  withdrawn: { label: "Withdrawn", icon: XCircle, cls: "bg-black/10 text-black/50" },
  expired: { label: "Expired", icon: XCircle, cls: "bg-black/10 text-black/50" },
  suspended: { label: "Suspended", icon: AlertCircle, cls: "bg-red-100 text-red-700" },
};

const deviceFingerprint = () => {
  const s = [navigator.userAgent, navigator.language, screen.width + "x" + screen.height,
    new Date().getTimezoneOffset(), navigator.hardwareConcurrency || ""].join("|");
  let h = 0; for (let i = 0; i < s.length; i++) { h = (h << 5) - h + s.charCodeAt(i); h |= 0; }
  return "fp_" + Math.abs(h).toString(36);
};

const StatusBadge = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.draft; const I = m.icon;
  return <span className={`inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${m.cls}`} data-testid="app-status-badge"><I className="w-3.5 h-3.5" /> {m.label}</span>;
};

const Field = ({ label, value, onChange, type = "text", options, required, testid }) => (
  <label className="block">
    <span className="text-xs font-bold uppercase tracking-wide text-black/60">{label}{required && <span className="text-taven-orange"> *</span>}</span>
    {options ? (
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full border border-black/15 px-3 py-2.5 text-sm bg-white" data-testid={testid}>
        <option value="">Select…</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    ) : (
      <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full border border-black/15 px-3 py-2.5 text-sm" data-testid={testid} />
    )}
  </label>
);

export default function Apply() {
  const { user, loading } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [programs, setPrograms] = useState(null);
  const [apps, setApps] = useState([]);
  const [active, setActive] = useState(null); // full application detail
  const [busy, setBusy] = useState(false);
  const [sig, setSig] = useState(""); const [consent, setConsent] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const saveTimer = useRef(null);
  const pendingPatch = useRef({});

  const planParam = params.get("plan");
  const programParam = params.get("program");

  const loadLists = useCallback(async () => {
    const [pg, mine] = await Promise.all([getApplicationPrograms(), getMyApplications()]);
    setPrograms(pg.programs); setApps(mine.applications);
    return { pg, mine };
  }, []);

  useEffect(() => {
    if (loading) return;
    if (!user) return;
    (async () => {
      const { mine } = await loadLists();
      // auto-start / resume when arriving from a plan or program link
      const prog = programParam || planToProgram(planParam);
      if (prog) {
        const existing = mine.applications.find((a) => a.program === prog && !["rejected", "withdrawn", "expired"].includes(a.status));
        if (existing) { openApp(existing.id); return; }
        try { const r = await startApplication(prog, planParam || undefined); openApp(r.application.id); } catch { /* */ }
      }
    })();
    // eslint-disable-next-line
  }, [user, loading]);

  const planToProgram = (plan) => {
    if (!plan) return null;
    if (plan === "wholesale") return "wholesale";
    if (plan.startsWith("dropshipping")) return "dropshipping";
    if (plan === "pro_ecommerce") return "pro_ecommerce";
    return null;
  };

  const openApp = async (id) => {
    const r = await getApplication(id); setActive(r.application);
    setSig(r.application.esign?.name || ""); setConsent(false); setAcceptedTerms(false);
  };

  const refreshActive = async () => { if (active) { const r = await getApplication(active.id); setActive(r.application); } await loadLists(); };

  const cfg = active?.config;
  const editable = active && ["draft", "more_info_required"].includes(active.status);

  const patch = (section, key, val) => {
    setActive((a) => ({ ...a, [section]: { ...(a[section] || {}), [key]: val } }));
    // accumulate a MERGED pending patch so rapid multi-field edits are not lost
    const pp = pendingPatch.current;
    pp[section] = { ...(pp[section] || {}), [key]: val };
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      const payload = pendingPatch.current; pendingPatch.current = {};
      try { await saveApplication(active.id, payload); } catch { /* */ }
    }, 700);
  };

  const doUpload = async (key, file) => {
    if (!file) return;
    setBusy(true);
    try { await uploadAppDocument(active.id, key, file); await refreshActive(); toast.success("Document uploaded"); }
    catch (e) { toast.error(e?.response?.data?.detail?.message || "Upload failed"); }
    finally { setBusy(false); }
  };

  const doSign = async () => {
    if (!sig.trim() || !consent || !acceptedTerms) { toast.error("Type your name, accept the terms and consent to e-sign."); return; }
    setBusy(true);
    try {
      await signApplication(active.id, { signature_name: sig.trim(), device_fingerprint: deviceFingerprint(), consent: true, language: localStorage.getItem("taven_lang") || "en" });
      await refreshActive(); toast.success("Signed");
    } catch (e) { toast.error("Could not record signature"); } finally { setBusy(false); }
  };

  const doSubmit = async () => {
    setBusy(true);
    try { const r = await submitApplication(active.id); await refreshActive(); toast.success(`Application ${r.status === "resubmitted" ? "resubmitted" : "submitted"}`); }
    catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(d?.reason === "incomplete" ? `Please complete: ${(d.missing || []).join(", ")}` : "Could not submit");
    } finally { setBusy(false); }
  };

  const goCheckout = async () => {
    setBusy(true);
    try { const { checkout_url } = await startSubCheckout(active.plan_id || cfg?.plan_ids?.[0]); window.location.href = checkout_url; }
    catch (e) { toast.error(e?.response?.data?.detail?.message || "Could not start checkout — please try again."); setBusy(false); }
  };

  if (!loading && !user) return (
    <div>
      <PageHero eyebrow="Applications" title="Sign in to apply" subtitle="Log in to start or continue a TAVEN program application." />
      <div className="px-4 py-12 text-center"><button onClick={() => navigate("/login", { state: { from: "/apply" } })} className="bg-taven-black text-white px-6 py-3 text-xs font-bold uppercase tracking-wider" data-testid="apply-login-cta">Sign In</button></div>
    </div>
  );

  // ---------- Wizard (active application) ----------
  if (active) {
    const missing = active.missing || [];
    const totalReqs = (cfg?.docs?.filter((d) => d.required).length || 0) + 4;
    const done = totalReqs - Math.min(missing.length, totalReqs);
    const pct = Math.round((done / totalReqs) * 100);
    return (
      <div data-testid="application-wizard">
        <PageHero eyebrow={cfg?.name || "Application"} title={`${cfg?.name} Application`} subtitle="Complete each section, sign, and submit for review." />
        <div className="px-4 md:px-8 lg:px-12 mx-auto max-w-4xl py-8">
          <button onClick={() => setActive(null)} className="text-xs font-bold uppercase tracking-wider text-black/50 hover:text-taven-orange mb-4" data-testid="app-back">← All Applications</button>

          <div className="flex flex-wrap items-center justify-between gap-3 border border-black/10 bg-white p-4 mb-6">
            <div className="flex items-center gap-3"><StatusBadge status={active.status} />
              <span className="text-xs text-black/50">ID {active.id.slice(0, 8)}</span></div>
            {editable && <div className="flex items-center gap-2 text-xs"><span className="text-black/50">{pct}% complete</span><div className="w-32 h-1.5 bg-black/10 rounded-full overflow-hidden"><div className="h-full bg-taven-gold" style={{ width: `${pct}%` }} /></div></div>}
          </div>

          {/* in-app draft reminder */}
          {active.reminder_note && ["draft", "more_info_required"].includes(active.status) && (
            <div className="border border-amber-300 bg-amber-50 p-3 mb-6 text-sm text-amber-800 flex items-center gap-2" data-testid="app-reminder">
              <AlertCircle className="w-4 h-4" /> {active.reminder_note}
            </div>
          )}
          {/* re-acceptance required (legal terms changed) */}
          {active.terms && active.esign?.completed && active.terms.some((t) => !(active.esign.terms || []).some((a) => a.id === t.id && a.content_hash === t.content_hash)) && (
            <div className="border border-orange-300 bg-orange-50 p-3 mb-6 text-sm text-orange-700 flex items-center gap-2" data-testid="app-reaccept">
              <RefreshCw className="w-4 h-4" /> Our legal terms were updated. Please review and re-sign below before continuing.
            </div>
          )}
          {/* admin request message */}
          {active.status === "more_info_required" && active.admin_messages?.length > 0 && (
            <div className="border border-orange-300 bg-orange-50 p-4 mb-6" data-testid="app-admin-message">
              <p className="text-xs font-bold uppercase tracking-wide text-orange-700 flex items-center gap-1"><AlertCircle className="w-4 h-4" /> More information requested</p>
              <p className="text-sm mt-1">{active.admin_messages[active.admin_messages.length - 1].message}</p>
            </div>
          )}
          {active.status === "rejected" && (
            <div className="border border-red-300 bg-red-50 p-4 mb-6" data-testid="app-reject-reason">
              <p className="text-xs font-bold uppercase tracking-wide text-red-700">Not approved</p>
              <p className="text-sm mt-1">{active.reject_reason}</p>
            </div>
          )}
          {active.status === "approved" && (
            <div className="border border-green-300 bg-green-50 p-5 mb-6 text-center" data-testid="app-approved-block">
              <ShieldCheck className="w-8 h-8 mx-auto text-green-600" />
              <p className="font-black font-display text-lg mt-2">Application Approved</p>
              {cfg?.billing ? (
                <>
                  <p className="text-sm text-black/60 mt-1">Authorize your paid membership to activate access.</p>
                  <button onClick={goCheckout} disabled={busy} className="mt-4 inline-flex items-center gap-1 bg-taven-black text-white px-6 py-3 text-xs font-bold uppercase tracking-wider hover:bg-taven-orange disabled:opacity-60" data-testid="app-continue-checkout">
                    {busy ? "Starting…" : "Continue to Payment"} <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </>
              ) : <p className="text-sm text-black/60 mt-1">Welcome to the program.</p>}
            </div>
          )}

          {/* Editable form sections */}
          {editable && (
            <>
              <Section title="Personal Information">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Full name" value={active.personal?.full_name} onChange={(v) => patch("personal", "full_name", v)} required testid="pf-full_name" />
                  <Field label="Phone" value={active.personal?.phone} onChange={(v) => patch("personal", "phone", v)} required testid="pf-phone" />
                  <Field label="Country" value={active.personal?.country} onChange={(v) => patch("personal", "country", v)} required testid="pf-country" />
                  <Field label="Address" value={active.personal?.address} onChange={(v) => patch("personal", "address", v)} required testid="pf-address" />
                  <Field label="City" value={active.personal?.city} onChange={(v) => patch("personal", "city", v)} required testid="pf-city" />
                  <Field label="State / Region" value={active.personal?.state} onChange={(v) => patch("personal", "state", v)} required testid="pf-state" />
                  <Field label="Postal code" value={active.personal?.postal_code} onChange={(v) => patch("personal", "postal_code", v)} required testid="pf-postal" />
                </div>
              </Section>

              {cfg?.needs_business && (
                <Section title="Business Information">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Business name" value={active.business?.business_name} onChange={(v) => patch("business", "business_name", v)} required testid="bf-name" />
                    <Field label="Business type" value={active.business?.business_type} onChange={(v) => patch("business", "business_type", v)} required testid="bf-type" />
                    <Field label="Tax ID / EIN" value={active.business?.tax_id} onChange={(v) => patch("business", "tax_id", v)} testid="bf-taxid" />
                    <Field label="Website" value={active.business?.website} onChange={(v) => patch("business", "website", v)} testid="bf-website" />
                  </div>
                </Section>
              )}

              <Section title="Program Details">
                <div className="grid sm:grid-cols-2 gap-4">
                  {cfg?.questions?.map((q) => (
                    <Field key={q.key} label={q.label} value={active.answers?.[q.key]} required={q.required}
                      type={q.type === "number" ? "number" : "text"} options={q.options}
                      onChange={(v) => patch("answers", q.key, v)} testid={`qf-${q.key}`} />
                  ))}
                </div>
              </Section>

              <Section title="Required Documents">
                <p className="text-xs text-black/50 mb-3">PDF, PNG, JPG or WEBP • max 10MB • stored securely and privately.</p>
                <div className="space-y-3">
                  {cfg?.docs?.map((d) => {
                    const uploaded = active.documents?.find((x) => x.key === d.key);
                    return (
                      <div key={d.key} className="flex items-center justify-between border border-black/10 p-3" data-testid={`doc-row-${d.key}`}>
                        <div><p className="text-sm font-bold">{d.label}{d.required && <span className="text-taven-orange"> *</span>}</p>
                          {uploaded && <p className="text-xs text-green-700 flex items-center gap-1 mt-0.5"><CheckCircle2 className="w-3.5 h-3.5" /> {uploaded.filename}</p>}</div>
                        <div className="flex items-center gap-2">
                          {uploaded && <button onClick={async () => { await deleteAppDocument(active.id, uploaded.doc_id); await refreshActive(); }} className="text-black/40 hover:text-red-600" data-testid={`doc-del-${d.key}`}><Trash2 className="w-4 h-4" /></button>}
                          <label className="cursor-pointer inline-flex items-center gap-1 border border-black/15 px-3 py-2 text-xs font-bold uppercase tracking-wide hover:bg-taven-gray" data-testid={`doc-upload-${d.key}`}>
                            <Upload className="w-3.5 h-3.5" /> {uploaded ? "Replace" : "Upload"}
                            <input type="file" className="hidden" onChange={(e) => doUpload(d.key, e.target.files[0])} accept=".pdf,.png,.jpg,.jpeg,.webp" />
                          </label>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Section>

              <Section title="Review Terms & E-Sign">
                <div className="max-h-40 overflow-y-auto border border-black/10 bg-taven-gray/40 p-3 text-xs space-y-1 mb-3" data-testid="terms-list">
                  {active.terms?.map((t) => (
                    <div key={t.id} className="flex items-center justify-between"><span>{t.title} <span className="text-black/40">v{t.version}</span></span><span className="text-black/30 font-mono">{t.content_hash.slice(0, 10)}…</span></div>
                  ))}
                </div>
                <label className="flex items-start gap-2 text-sm mb-2"><input type="checkbox" checked={acceptedTerms} onChange={(e) => setAcceptedTerms(e.target.checked)} className="mt-1" data-testid="accept-terms" /> I have read and accept the TAVEN legal documents above (current versions).</label>
                <label className="flex items-start gap-2 text-sm mb-3"><input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1" data-testid="accept-esign" /> I consent to electronic records and that my typed name is my legally binding electronic signature.</label>
                <div className="flex items-end gap-3">
                  <div className="flex-1"><Field label="Type your full legal name to sign" value={sig} onChange={setSig} testid="sig-input" /></div>
                  <button onClick={doSign} disabled={busy} className="inline-flex items-center gap-1 border border-taven-gold bg-taven-gold/10 px-4 py-2.5 text-xs font-bold uppercase tracking-wide hover:bg-taven-gold/20 disabled:opacity-50" data-testid="sign-btn"><PenLine className="w-3.5 h-3.5" /> Sign</button>
                </div>
                {active.esign?.completed && <p className="text-xs text-green-700 mt-2 flex items-center gap-1" data-testid="esign-done"><CheckCircle2 className="w-3.5 h-3.5" /> Signed by {active.esign.name} • hash {active.esign.acceptance_hash?.slice(0, 12)}…</p>}
              </Section>

              <div className="flex flex-wrap items-center justify-between gap-3 mt-6">
                <button onClick={async () => { if (window.confirm("Withdraw this application?")) { await withdrawApplication(active.id); await refreshActive(); } }} className="text-xs font-bold uppercase tracking-wider text-black/40 hover:text-red-600" data-testid="app-withdraw">Withdraw</button>
                <button onClick={doSubmit} disabled={busy || missing.length > 0} className="inline-flex items-center gap-1 bg-taven-black text-white px-6 py-3 text-xs font-bold uppercase tracking-wider hover:bg-taven-orange disabled:opacity-40" data-testid="app-submit">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} {active.status === "more_info_required" ? "Resubmit" : "Submit for Review"} <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
              {missing.length > 0 && <p className="text-xs text-taven-orange text-right mt-2" data-testid="app-missing">Still needed: {missing.length} item(s)</p>}
            </>
          )}

          {/* Read-only status tracking */}
          {!editable && active.status !== "approved" && active.status !== "rejected" && (
            <div className="border border-black/10 bg-white p-5" data-testid="app-tracking">
              <p className="text-sm text-black/60">Your application is <strong>{STATUS_META[active.status]?.label}</strong>. We'll email you and update this page as it progresses.</p>
            </div>
          )}

          {/* Timeline + receipt */}
          <div className="mt-8 grid md:grid-cols-2 gap-6">
            <div className="border border-black/10 bg-white p-5">
              <h3 className="font-black font-display uppercase text-sm tracking-wide mb-3">Timeline</h3>
              <ol className="space-y-2" data-testid="app-timeline">
                {(active.timeline || []).map((t, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm"><ChevronRight className="w-4 h-4 text-taven-gold mt-0.5 shrink-0" /><div><span className="font-bold">{STATUS_META[t.status]?.label || t.status}</span> — <span className="text-black/60">{t.note}</span><br /><span className="text-[11px] text-black/40">{new Date(t.at).toLocaleString()}</span></div></li>
                ))}
              </ol>
            </div>
            <div className="border border-black/10 bg-white p-5">
              <h3 className="font-black font-display uppercase text-sm tracking-wide mb-3">Signed Receipt</h3>
              {active.esign?.completed ? (
                <button onClick={() => downloadAppReceipt(active.id)} className="inline-flex items-center gap-1 border border-black/15 px-4 py-2.5 text-xs font-bold uppercase tracking-wide hover:bg-taven-gray" data-testid="app-receipt"><Download className="w-3.5 h-3.5" /> Download PDF Receipt</button>
              ) : <p className="text-sm text-black/50">Available after you sign.</p>}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---------- Dashboard ----------
  return (
    <div data-testid="applications-dashboard">
      <PageHero eyebrow="Applications" title="Your TAVEN Applications" subtitle="Apply to a program, track status, and continue where you left off." />
      <div className="px-4 md:px-8 lg:px-12 mx-auto max-w-5xl py-8">
        {apps.length > 0 && (
          <div className="mb-10">
            <h2 className="font-black font-display uppercase text-sm tracking-wide mb-3">In Progress & Submitted</h2>
            <div className="grid gap-3" data-testid="my-applications">
              {apps.map((a) => (
                <button key={a.id} onClick={() => openApp(a.id)} className="flex items-center justify-between border border-black/10 bg-white p-4 hover:border-taven-gold text-left" data-testid={`app-card-${a.program}`}>
                  <div><p className="font-bold">{(programs?.find((p) => p.key === a.program)?.name) || a.program}</p><p className="text-xs text-black/40">Updated {new Date(a.updated_at).toLocaleDateString()}</p></div>
                  <div className="flex items-center gap-3"><StatusBadge status={a.status} /><ArrowRight className="w-4 h-4 text-black/30" /></div>
                </button>
              ))}
            </div>
          </div>
        )}
        <h2 className="font-black font-display uppercase text-sm tracking-wide mb-3">Start a New Application</h2>
        {!programs ? <div className="h-40 bg-black/5 animate-pulse" /> : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="program-picker">
            {programs.map((p) => (
              <div key={p.key} className="border border-black/10 bg-white p-5 flex flex-col">
                <FileText className="w-6 h-6 text-taven-gold" />
                <h3 className="font-black font-display mt-2">{p.name}</h3>
                <p className="text-xs text-black/50 mt-1 flex-1">{p.billing ? "Paid membership program" : "Partner / creator program"} • {p.questions.length} questions • {p.docs.filter((d) => d.required).length} required doc(s)</p>
                <button onClick={async () => { try { const r = await startApplication(p.key); openApp(r.application.id); } catch { toast.error("Could not start"); } }} className="mt-4 inline-flex items-center justify-center gap-1 bg-taven-black text-white px-4 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-taven-orange" data-testid={`start-${p.key}`}>
                  Apply <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const Section = ({ title, children }) => (
  <div className="border border-black/10 bg-white p-5 mb-5">
    <h3 className="font-black font-display uppercase text-sm tracking-wide mb-4">{title}</h3>
    {children}
  </div>
);
